/**
 * 设定API服务
 *
 * 封装设定相关的HTTP API调用
 * 提供设定CRUD操作: listSettings, createSetting, getSetting, updateSetting, deleteSetting, searchSettings
 * 使用 HttpOnly Cookie 进行认证。
 */

import { settingApi, HttpError } from './settingApi';
import type { Setting, SettingCreate, SettingUpdate } from '../types/setting';

export class ApiError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ValidationError extends ApiError {
  constructor(message: string) {
    super(message, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

export class NotFoundError extends ApiError {
  constructor(message: string) {
    super(message, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class ForbiddenError extends ApiError {
  constructor(message: string) {
    super(message, 'FORBIDDEN');
    this.name = 'ForbiddenError';
  }
}

export class DuplicateNameError extends ApiError {
  constructor(message: string) {
    super(message, 'DUPLICATE_NAME');
    this.name = 'DuplicateNameError';
  }
}

function handleError(error: unknown): never {
  if (error instanceof HttpError) {
    switch (error.status) {
      case 404:
        throw new NotFoundError(error.message);
      case 403:
        throw new ForbiddenError(error.message);
      case 400:
        throw new ValidationError(error.message);
      default:
        throw new ApiError(error.message, error.code);
    }
  }
  throw error;
}

export class SettingService {
  private workspaceId: number;

  constructor(
    _wsService?: unknown,
    _token?: string,
    workspaceId?: number
  ) {
    this.workspaceId = workspaceId || 0;
  }

  setWorkspaceId(workspaceId: number): void {
    this.workspaceId = workspaceId;
  }

  async listSettings(
    _workspaceId: number,
    _filters?: { name?: string; level?: string }
  ): Promise<Setting[]> {
    try {
      return await settingApi.listSettings(this.workspaceId);
    } catch (error) {
      handleError(error);
    }
  }

  async createSetting(_workspaceId: number, data: SettingCreate): Promise<Setting> {
    try {
      return await settingApi.createSetting(this.workspaceId, data);
    } catch (error) {
      handleError(error);
    }
  }

  async getSetting(_workspaceId: number, settingId: number): Promise<Setting> {
    try {
      return await settingApi.getSetting(settingId);
    } catch (error) {
      handleError(error);
    }
  }

  async updateSetting(
    _workspaceId: number,
    settingId: number,
    data: SettingUpdate
  ): Promise<Setting> {
    try {
      return await settingApi.updateSetting(settingId, data);
    } catch (error) {
      handleError(error);
    }
  }

  async deleteSetting(_workspaceId: number, settingId: number): Promise<void> {
    try {
      await settingApi.deleteSetting(settingId);
    } catch (error) {
      handleError(error);
    }
  }

  async lockSetting(_workspaceId: number, settingId: number, action: 'lock' | 'unlock'): Promise<Setting> {
    try {
      return await settingApi.lockSetting(settingId, action);
    } catch (error) {
      handleError(error);
    }
  }

  async searchSettings(_workspaceId: number, names: string | string[]): Promise<Setting[]> {
    try {
      const namesArray = Array.isArray(names) ? names : [names];
      return await settingApi.searchSettings(this.workspaceId, namesArray);
    } catch (error) {
      handleError(error);
    }
  }

  async getTokenUsage(_workspaceId: number): Promise<{
    total_tokens: number;
    total_settings: number;
    available_tokens: number;
  }> {
    try {
      return await settingApi.getTokenUsage(this.workspaceId);
    } catch (error) {
      handleError(error);
    }
  }
}

export default SettingService;
